/** Q73 — 서버 착용 슬롯 순서와 장비 효과 미러를 사용하는 표시 전용 비교. */
import { wearSlotsOf, SLOT_NAME } from '../world/gearnames.js';
import { MAX_GRADE } from '../world/map.js';
import { eqName, equipmentBonus, gearTone, isTempleRingItem } from './panels/common.js';

const LABELS = { atk: '공격력', def: '방어력', move: '이동속도', haste: '공격속도', regen: '자연회복', evade: '회피', stun: '기절 확률', allStats: '모든 능력치' };
const PERCENT = new Set(['move', 'haste', 'regen', 'evade', 'stun']);
const number = n => n.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
const value = (key, n) => n == null ? '비공개' : number(PERCENT.has(key) ? n * 100 : n) + (PERCENT.has(key) ? '%' : '');

export function compareTarget(y, e) {
  const equipped = y.inv?.equipped || {}, slots = wearSlotsOf(e.slot);
  const slot = slots.find(k => equipped[k] === e.id) ?? slots.find(k => !equipped[k]) ?? slots[0];
  return { slot, current: (y.inv?.equips || []).find(q => q.id === equipped[slot]) || null };
}

export function comparisonValues(e, y) {
  const out = { ...equipmentBonus(e), stun: 0, allStats: 0 };
  if (e?.slot === 'ring' && e.guildBound != null) {
    const r = y.guildRing;
    const table = (e.grade || 0) > (r?.grade ?? MAX_GRADE - 1) ? r?.ultimateChanceByPlus : r?.chanceByPlus;
    out.stun = Array.isArray(table) && table.length ? table[Math.max(0, Math.min(table.length - 1, e.plus || 0))] : null;
  }
  if (isTempleRingItem(e)) {
    const n = y.templeRing?.revealed ? y.templeRing.perStat?.[e.id] : null;
    out.allStats = Number.isFinite(n) ? n : null;
  }
  return out;
}

/** Ordinary gear uses the same rolled effect mirror as equipped comparisons. Private ring values are never guessed. */
export function enhancementEffects(e, y, minPlus, maxPlus = minPlus) {
  const before = comparisonValues(e, y);
  const after = [minPlus, maxPlus].map((plus) => comparisonValues({ ...e, plus }, y));
  if (isTempleRingItem(e)) for (const out of after) out.allStats = null;
  const keys = Object.keys(LABELS).filter((key) => before[key] !== 0 || after.some((out) => out[key] !== 0));
  return keys.map((key) => {
    const range = after[0][key] == null || after[1][key] == null ? '강화 후 확인'
      : after[0][key] === after[1][key] ? value(key, after[0][key]) : `${value(key, after[0][key])}~${value(key, after[1][key])}`;
    return `<div class="kv"><span>${LABELS[key]}</span><b>${value(key, before[key])} → ${range}</b></div>`;
  }).join('')
    + (e.guildBound != null ? '<div class="qt">기절 확률 안전선 위 강화는 기절 시간을 늘린다. 시간 증가량은 강화 후 확인한다.</div>' : '')
    + '<div class="qt">장비 자체 효과 · 캐릭터 최종 능력치는 착용 후 적용</div>';
}

export function equipmentComparison(e, y) {
  const { slot, current } = compareTarget(y, e);
  if (!slot || current?.id === e.id) return '';
  const before = comparisonValues(current, y), after = comparisonValues(e, y);
  const keys = Object.keys(LABELS).filter(k => before[k] !== 0 || after[k] !== 0);
  const cellStyle = 'min-width:0;overflow-wrap:anywhere;padding:6px;border:1px solid #68593b;border-radius:4px';
  // F19 — 두 장비의 등급을 왼쪽 띠(등급색)로도 — 가방·대장간 칸 테두리와 같은 색
  const heading = (item, selected) => `<div style="${cellStyle}${item ? `;border-left:4px solid ${gearTone(item).tint}` : ''}"><b>${selected ? '선택 장비' : '현재 착용'}</b>`
    + `<div style="margin:5px 0">${item ? eqName(item) : `${SLOT_NAME[slot] || '장비'} 없음`}</div></div>`;
  const stat = (k, values, selected) => {
      const delta = values[k] == null || before[k] == null ? null : values[k] - before[k];
      const changed = selected && delta != null && Math.abs(delta) > 1e-9;
      return `<div style="${cellStyle}"><span>${LABELS[k]}</span><br><b>${value(k, values[k])}</b>`
        + (changed ? ` <small style="color:${delta > 0 ? '#86db99' : '#ff9990'}">(${delta > 0 ? '+' : '−'}${number(Math.abs(delta) * (PERCENT.has(k) ? 100 : 1))}${PERCENT.has(k) ? '%p' : ''})</small>` : '') + '</div>';
  };
  return '<div class="equipment-compare" aria-label="현재 착용 장비와 선택 장비 비교" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;margin:6px 0">'
    + heading(current, false) + heading(e, true)
    + keys.map(k => stat(k, before, false) + stat(k, after, true)).join('') + '</div>'
    + '<div class="qt">장비 자체 효과 비교 · 캐릭터 최종 능력치는 착용 후 적용</div>';
}
